import { ReactElement, ReactNode, cloneElement } from 'react'
import { Box } from '@mui/material'
import { Link } from 'react-router-dom'

export type SidebarItem = {
  label: string
  to: string
  icon: ReactElement<{ sx?: object }>
  /** Decided by the caller from the URL, never held in component state. */
  active: boolean
}

export type SidebarGroup = {
  label: string
  items: SidebarItem[]
  /** Numbered groups are a sequence (inputs, then plan, then outputs). A
   *  group outside that sequence — plain navigation — passes false. */
  numbered?: boolean
}

/**
 * The shell's permanent left navigation (design system: Sidebar): 230px on
 * `panel`, numbered groups, ~27px items, a 3px teal edge on the current one.
 * A `Box`, not a `Drawer` — it is never dismissed. It scrolls on its own;
 * the content column beside it scrolls separately.
 *
 * Never put a page action in here. Actions live in the page header.
 */
export const Sidebar = ({
  groups,
  label = 'Sections',
  header,
}: {
  groups: SidebarGroup[]
  label?: string
  /** Above the groups — e.g. the scenario switcher. */
  header?: ReactNode
}) => {
  let step = 0
  return (
    <Box
      component="nav"
      aria-label={label}
      sx={{
        width: 'var(--sidebar-w)',
        flexShrink: 0,
        minHeight: 0,
        overflowY: 'auto',
        background: 'var(--panel)',
        borderRight: '1px solid var(--line-300)',
        py: '12px',
      }}
    >
      {header && <Box sx={{ px: '12px', pb: '14px' }}>{header}</Box>}
      {groups.map((group) => {
        const numbered = group.numbered !== false
        if (numbered) step += 1
        return (
          <Box key={group.label} sx={{ mb: '14px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                mx: '12px',
                mb: '6px',
                pb: '6px',
                borderBottom: numbered
                  ? '2px solid var(--brand-teal)'
                  : '1px solid var(--line-300)',
              }}
            >
              {numbered && (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--brand-teal)',
                    color: 'var(--surface-0)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {step}
                </Box>
              )}
              <Box
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-700)',
                }}
              >
                {group.label}
              </Box>
            </Box>

            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {group.items.map((item) => (
                <li key={item.to}>
                  <Box
                    component={Link}
                    to={item.to}
                    aria-current={item.active ? 'page' : undefined}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      mx: '6px',
                      py: '5px',
                      pl: item.active ? '7px' : '10px',
                      pr: '10px',
                      borderLeft: item.active
                        ? '3px solid var(--brand-teal)'
                        : '3px solid transparent',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 12.5,
                      fontWeight: item.active ? 600 : 400,
                      color: item.active
                        ? 'var(--brand-teal)'
                        : 'var(--ink-700)',
                      background: item.active
                        ? 'rgba(0,65,90,0.08)'
                        : 'transparent',
                      textDecoration: 'none',
                      '&:hover': {
                        background: item.active
                          ? 'rgba(0,65,90,0.08)'
                          : 'rgba(0,65,90,0.04)',
                      },
                    }}
                  >
                    {cloneElement(item.icon, {
                      sx: {
                        fontSize: 15,
                        color: item.active
                          ? 'var(--brand-teal)'
                          : 'var(--ink-400)',
                      },
                    })}
                    {item.label}
                  </Box>
                </li>
              ))}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
